import type { FeatureFlagKey } from "@erp/shared-types";
import { IntegrationKey } from "@erp/shared-types";

export enum VisibilityPredicateType {
  Admin = "admin",
  FeatureFlag = "featureFlag",
  Integration = "integration",
  InUse = "inUse",
  ServerInfrastructureConfig = "serverInfrastructureConfig",
}

export enum ServerInfrastructureConfigKey {
  BrowserUse = "browserUse",
}

export type AdminPredicate = {
  type: VisibilityPredicateType.Admin;
};

export type FeatureFlagPredicate = {
  type: VisibilityPredicateType.FeatureFlag;
  flag: FeatureFlagKey;
};

export type IntegrationPredicate = {
  type: VisibilityPredicateType.Integration;
  integration: IntegrationKey;
};

/**
 * **System-reserved.** Do not use in `StepConfig.visibility` — it is appended
 * automatically by `VisibilityService.evaluateStep` for steps present in the
 * current workflow. Using it in config would make the step unconditionally visible.
 */
export type InUsePredicate = {
  type: VisibilityPredicateType.InUse;
};

export type ServerInfrastructureConfigPredicate = {
  type: VisibilityPredicateType.ServerInfrastructureConfig;
  key: ServerInfrastructureConfigKey;
};

/**
 * Discriminated union of all visibility predicate types.
 *
 * Each predicate evaluates a single condition (admin status, feature flag, etc.).
 * Predicates are composed into groups using {@link VisibilityRule}.
 */
export type VisibilityPredicate =
  | AdminPredicate
  | FeatureFlagPredicate
  | IntegrationPredicate
  | InUsePredicate
  | ServerInfrastructureConfigPredicate;

/**
 * Visibility rule for a workflow step, expressed in disjunctive normal form (DNF).
 *
 * - The outer array represents OR groups — if **any** group passes, the step is visible.
 * - Each inner array represents an AND group — **all** predicates in the group must pass.
 *
 * @example
 * // Visible to admins who also have the feature flag enabled:
 * const rule: VisibilityRule = [
 *   [{ type: VisibilityPredicateType.Admin }, { type: VisibilityPredicateType.FeatureFlag, flag: "my-flag" }],
 * ];
 *
 * @example
 * // Visible to admins, OR to anyone with the integration enabled:
 * const rule: VisibilityRule = [
 *   [{ type: VisibilityPredicateType.Admin }],
 *   [{ type: VisibilityPredicateType.Integration, integration: IntegrationKey.Famous }],
 * ];
 *
 * @example
 * // Single condition — still requires the outer+inner array:
 * const rule: VisibilityRule = [
 *   [{ type: VisibilityPredicateType.Integration, integration: IntegrationKey.Famous }],
 * ];
 */
export type VisibilityRule = VisibilityPredicate[][];

export type PredicateResult = {
  type: VisibilityPredicateType;
  passed: boolean;
  detail: string;
};

export type VisibilityState = "visible" | "hidden" | "restricted" | "disabled";

/**
 * Visibility evaluation result attached to a step type in API responses.
 *
 * - `"visible"` — at least one predicate group passes (or no predicates defined).
 * - `"hidden"` — all predicate groups fail and the step is not in use. Non-admins
 *   never receive hidden steps; admins receive them with predicate results.
 * - `"restricted"` — the step is in use in the current workflow but all "real"
 *   predicate groups fail. Only the system-appended `inUse` group passes.
 *   Returned with limited info (name, inputs, outputs) so the editor can render
 *   the node but the user cannot re-add it to new workflows.
 *
 * `predicateResults` mirrors the `VisibilityRule` (outer=OR, inner=AND) with
 * pass/fail status for each predicate. Only present for admin users.
 *
 * `disabledReason` is set when `visibility === "disabled"` — a short message
 * explaining why the item cannot be used (e.g. missing server infrastructure).
 */
export type StepVisibilityResult = {
  visibility: VisibilityState;
  predicateResults?: PredicateResult[][];
  disabledReason?: string;
};
